import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getApiUrl } from '../../config/api';
import { LanguageContext } from '../../contexts/LanguageContext';
import FlowMoversPanel from './FlowMoversPanel';
import { takeEdgeSymbol } from '../../utils/edgeHandoff';
import { getChartSymbol, resolveSymbol } from '../../services/symbolPricingService';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import './FlowTape.ds.css';
import { ToolState } from '../mmec/ToolState';

/**
 * FlowTape — per-strike option order-flow for the RTL (legacy) source, one RTH session at a
 * time. Replaces the heavy Analytics Hub: it asks the backend for the net-flow series of ONLY
 * the strikes you select (/api/rtl-flow/series), scoped to a single trading day's Regular
 * Trading Hours, and draws them as accumulating lines with real zoom/pan. RTL source only.
 */

type Measure = 'delta' | 'premium' | 'trades';
type Basis = 'accumulated' | '5min';
interface FlowPoint { t: number; cNet: number; pNet: number; net: number; buy: number; sell: number; cBuy: number; cSell: number; pBuy: number; pSell: number; }
type Breakdown = 'net' | 'callput' | 'buysell';
type Layout = 'one' | 'two';           // one overlaid chart, or two stacked panes (calls above, puts below)
type PaneKey = 'C' | 'P';
interface PaneY { vMin: number; vMax: number; }
const MEASURES: [Measure, string][] = [['delta', 'Delta'], ['premium', 'Premium'], ['trades', 'Trades']];
const MEASURE_UNIT: Record<Measure, string> = { delta: 'NET CONTRACTS', premium: 'NET PREMIUM $', trades: 'NET TRADES' };
const MEASURE_NAME: Record<Measure, string> = { delta: 'net delta (ask−bid volume)', premium: 'net premium $', trades: 'net trade count' };
interface LadderStrike { key: string; strike: number; net: number; cNet: number; pNet: number; cVol: number; pVol: number; }
interface ChartSeries { label: string; color: string; dash: number[]; width: number; vals: { t: number; v: number }[]; pane?: PaneKey; }
interface Domain { tMin: number; tMax: number; vMin: number; vMax: number; }

/* Canvas palette. `let` only so the Design System preview can swap it at render time (canvas cannot read CSS
   vars); members keep exactly these values. */
let SERIES = ['#3f8ae6', '#d95926', '#199e70', '#c98500', '#d55181', '#9085e9'];
let BULL = '#16b981', BEAR = '#f0555f', MUTED = '#8b94a4', MUT2 = '#5b6472', LINE = '#262d3a', GRID = '#1b212b';
/** Text-on-ground direction colours (DS: --up-text / --down-text); same as BULL/BEAR for members. */
let BULL_T = BULL, BEAR_T = BEAR;
/** Canvas ground (dot rims, label backs) and ink (axis labels) as r,g,b. */
let CV_MONO = '"IBM Plex Mono", ui-monospace, monospace';
let INK_BG = '#0e1015', INK_BG_RGB = '14,16,21', INK_RGB = '231,235,242';
function applyFtPalette(): void {
  if (!isDesignSystemOn()) return;
  SERIES = ['#42a5f5', '#ffa726', '#80deea', '#ce93d8', '#7986cb', '#b388ff'];
  BULL = '#26a69a'; BEAR = '#ef5350'; MUTED = '#9598a1'; MUT2 = '#787b86'; LINE = '#2a2e39'; GRID = '#1e222d';
  BULL_T = '#4dd0c4'; BEAR_T = '#ff8a85';
  INK_BG = '#131722'; INK_BG_RGB = '19,23,34'; INK_RGB = '209,212,220';
  UND_COLOR = '#b2b5be';
  CV_MONO = '"JetBrains Mono", ui-monospace, monospace';
}
const POLL_MS = 7000;

const fmtK = (v: number) => { const a = Math.abs(v); if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 0 : 1) + 'M'; if (a >= 1000) return (v / 1000).toFixed(a >= 10000 ? 0 : 1) + 'k'; return String(Math.round(v)); };
const fmtSign = (v: number) => (v > 0 ? '+' : '') + fmtK(v);
const cls = (v: number) => (v > 0.5 ? 'ft-pos' : v < -0.5 ? 'ft-neg' : 'ft-zero');
const etTime = (ms: number) => new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' });
const dayLabel = (d: string) => { try { const dt = new Date(d + 'T12:00:00'); return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); } catch { return d; } };

async function jget(url: string): Promise<any> {
  try { const r = await fetch(getApiUrl(url), { credentials: 'include' }); if (!r.ok) return { success: false, status: r.status }; return await r.json(); }
  catch { return { success: false }; }
}

// ── Two panes (calls above, puts below) ─────────────────────────────────────
// One canvas, two plot rects: shared x-axis, zoom/pan view and crosshair; each pane autoscales on
// its own and draws its own zero line. Purely additive — draw() branches here before the one-chart path.
const PANE_COPY = {
  en: { one: 'One chart', two: 'Two panes', hint: 'One chart (lines overlaid) or two stacked panes — calls above, puts below', C: 'CALLS', P: 'PUTS', tipC: 'Calls', tipP: 'Puts' },
  ar: { one: 'رسم واحد', two: 'لوحتان', hint: 'رسم واحد أو لوحتان متراكبتان — كول فوق وبوت تحت', C: 'كول', P: 'بوت', tipC: 'كول', tipP: 'بوت' },
};
// ── Underlying price line ───────────────────────────────────────────────────
// The session's 1-minute bars (index roots on their native index scale), drawn as one thin ink
// line on its own right-hand price axis so flow and price can be read against each other.
// Flow keeps the left axis; the price axis autoscales to the bars inside the visible time range.
interface UndBar { t: number; c: number; }
interface UndOpts { bars: UndBar[]; sym: string; name: string; below: boolean; }   // below = own pane under the flow chart
let UND_COLOR = '#e7ebf2';
const UND_KEY = 'mmecFlowTapeUnderlying';
const UND_LAYOUT_KEY = 'mmecFlowTapeUnderlayLayout';
const UND_BELOW_RATIO = 0.35;           // price pane height relative to the flow chart
const UND_PAD = 52;                    // extra right padding that holds the price axis
const UND_COPY = {
  en: { name: 'Underlying', hint: 'Show the underlying price on its own right-hand axis', over: 'Overlay', below: 'Below', layHint: 'Draw the price over the flow lines, or in its own pane below on the same time axis' },
  ar: { name: 'السعر الأساسي', hint: 'إظهار سعر الأصل على محور أيمن مستقل', over: 'فوق الرسم', below: 'لوحة أسفل', layHint: 'رسم السعر فوق خطوط التدفق أو في لوحة مستقلة أسفلها على المحور الزمني نفسه' },
};
const fmtPx = (v: number, step = 0.01) => v.toFixed(step >= 1 ? 0 : step >= 0.1 ? 1 : 2);
/** Last bar at or before t (bars are sorted); null when none, or the nearest is over 6 minutes old. */
function undAt(bars: UndBar[], t: number): UndBar | null {
  let lo = 0, hi = bars.length - 1, best = -1;
  while (lo <= hi) { const m = (lo + hi) >> 1; if (bars[m].t <= t) { best = m; lo = m + 1; } else hi = m - 1; }
  if (best < 0) return null;
  return t - bars[best].t > 6 * 60_000 ? null : bars[best];
}
/** Draws the price line + its right-hand axis for one plot rect. Labels sit at the canvas's right edge. */
function drawUnderlying(ctx: CanvasRenderingContext2D, u: UndOpts, X: (t: number) => number, tMin: number, tMax: number,
  plotL: number, plotR: number, top: number, h: number, CW: number, ticks: number, niceStep: (rng: number, target: number) => number, title: boolean, grid = false): ((p: number) => number) | null {
  const span = tMax - tMin; const vis = u.bars.filter(b => b.t >= tMin - span * 0.02 && b.t <= tMax + span * 0.02);
  if (vis.length < 2) return null;
  let pMin = Infinity, pMax = -Infinity; for (const b of vis) { if (b.c < pMin) pMin = b.c; if (b.c > pMax) pMax = b.c; }
  if (pMin === pMax) { pMin -= 0.5; pMax += 0.5; }
  const pad = (pMax - pMin) * 0.1; pMin -= pad; pMax += pad;
  const PY = (p: number) => top + (1 - (p - pMin) / (pMax - pMin)) * h;
  const MONO = CV_MONO;
  const ps = niceStep(pMax - pMin, ticks);
  ctx.save(); ctx.beginPath(); ctx.rect(plotL, top, plotR - plotL, h); ctx.clip();
  if (grid) for (let gv = Math.ceil(pMin / ps) * ps; gv <= pMax; gv += ps) { const y = PY(gv); ctx.strokeStyle = GRID; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(plotL, y + 0.5); ctx.lineTo(plotR, y + 0.5); ctx.stroke(); }
  ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.setLineDash([]);
  ctx.beginPath(); vis.forEach((b, i) => { const x = X(b.t), y = PY(b.c); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
  ctx.strokeStyle = UND_COLOR; ctx.lineWidth = 1.5; ctx.globalAlpha = 0.82; ctx.stroke(); ctx.globalAlpha = 1;
  ctx.restore();
  // axis: ticks + labels in the price's own scale, no extra grid (the flow grid stays the only one)
  const last = vis[vis.length - 1]; const lastIn = last.t <= tMax; const ly = PY(last.c);
  const tickX = grid ? plotR + 2 : CW - UND_PAD + 2;
  ctx.font = '500 10px ' + MONO; ctx.textBaseline = 'middle'; ctx.textAlign = 'right';
  for (let gv = Math.ceil(pMin / ps) * ps; gv <= pMax; gv += ps) {
    const y = PY(gv); if (y < top + 4 || y > top + h - 4) continue; if (lastIn && Math.abs(y - ly) < 12) continue;
    ctx.fillStyle = `rgba(${INK_RGB},0.5)`; ctx.fillText(fmtPx(gv, ps), CW - 6, y);
    ctx.strokeStyle = `rgba(${INK_RGB},0.22)`; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(tickX, y + 0.5); ctx.lineTo(tickX + 4, y + 0.5); ctx.stroke();
  }
  if (title) { ctx.font = '600 9px ' + MONO; ctx.fillStyle = `rgba(${INK_RGB},0.6)`; ctx.fillText(u.sym, CW - 6, top - 6); }
  // last-price tag on the axis
  if (lastIn && ly >= top - 2 && ly <= top + h + 2) {
    const txt = fmtPx(last.c); ctx.font = '700 10px ' + MONO; const tw = ctx.measureText(txt).width + 10; const x0 = CW - 4 - tw;
    ctx.fillStyle = UND_COLOR; ctx.beginPath(); (ctx as any).roundRect ? (ctx as any).roundRect(x0, ly - 7.5, tw, 15, 3) : ctx.rect(x0, ly - 7.5, tw, 15); ctx.fill();
    ctx.fillStyle = INK_BG; ctx.textAlign = 'left'; ctx.fillText(txt, x0 + 5, ly + 0.5);
    if (X(last.t) >= plotL && X(last.t) <= plotR) { ctx.beginPath(); ctx.arc(X(last.t), ly, 2.6, 0, 7); ctx.fillStyle = UND_COLOR; ctx.fill(); }
  }
  return PY;
}
const undTipRow = (u: UndOpts | null | undefined, t: number) => {
  if (!u) return ''; const b = undAt(u.bars, t); if (!b) return '';
  return `<div class="ft-tr ft-tund"><span class="ft-lg"><span class="ft-sw" style="background:${UND_COLOR};height:2px;border-radius:1px"></span>${u.name} · ${u.sym}</span><span class="ft-val" style="color:${UND_COLOR}">${fmtPx(b.c)}</span></div>`;
};

interface PaneRect { key: PaneKey; top: number; h: number; vMin: number; vMax: number; Y: (v: number) => number; invY: (py: number) => number; }
const paneAt = (panes: PaneRect[], y: number): PaneRect => panes.find(p => y >= p.top && y <= p.top + p.h) || panes[y < panes[1].top ? 0 : 1];
function paneAutoY(s: ChartSeries[], key: PaneKey): PaneY {
  let vMin = Infinity, vMax = -Infinity;
  for (const ss of s) { if (ss.pane !== key) continue; for (const p of ss.vals) { if (p.v < vMin) vMin = p.v; if (p.v > vMax) vMax = p.v; } }
  if (!isFinite(vMin) || !isFinite(vMax)) { vMin = -1; vMax = 1; }
  if (vMin === vMax) { vMin -= 1; vMax += 1; }
  const pad = (vMax - vMin) * 0.12; vMin -= pad; vMax += pad; if (vMin > 0) vMin = 0; if (vMax < 0) vMax = 0;
  return { vMin, vMax };
}
interface TwoPaneOpts { view: Domain | null; paneY: Partial<Record<PaneKey, PaneY>>; hov: number | null; hi: Set<string>; tip: HTMLDivElement | null; unit: string; copy: typeof PANE_COPY.en; niceStep: (rng: number, target: number) => number; und?: UndOpts | null; }
function drawTwoPanes(ctx: CanvasRenderingContext2D, s: ChartSeries[], CW: number, CH: number, o: TwoPaneOpts) {
  const { view, paneY, hov, hi, tip, unit, copy, niceStep, und } = o;
  const MONO = CV_MONO;
  const padL = 58, padR = 66 + (und && !und.below ? UND_PAD : 0), padT = 16, padB = 26, GAP = 24, w = CW - padL - padR;
  const hp = Math.max(24, (CH - padT - padB - GAP) / 2);
  // shared time axis (the zoom/pan view keeps only its t-range here; y is per pane)
  let tMin = Infinity, tMax = -Infinity;
  if (view) { tMin = view.tMin; tMax = view.tMax; }
  else { for (const ss of s) for (const p of ss.vals) { if (p.t < tMin) tMin = p.t; if (p.t > tMax) tMax = p.t; } if (tMin === tMax) tMax = tMin + 1; }
  const X = (t: number) => padL + (t - tMin) / (tMax - tMin) * w;
  const invX = (px: number) => tMin + (px - padL) / w * (tMax - tMin);
  const panes: PaneRect[] = (['C', 'P'] as PaneKey[]).map((key, i) => {
    const top = padT + i * (hp + GAP); const { vMin, vMax } = paneY[key] || paneAutoY(s, key);
    return { key, top, h: hp, vMin, vMax, Y: (v: number) => top + (1 - (v - vMin) / (vMax - vMin)) * hp, invY: (py: number) => vMax - (py - top) / hp * (vMax - vMin) };
  });
  // one pane: grid + y labels, lines, end labels, high markers — called once per pane
  const drawPane = (pn: PaneRect) => {
    const { top, h, vMin, vMax, Y } = pn; const lines = s.filter(ss => ss.pane === pn.key);
    ctx.font = '500 10px ' + MONO; ctx.textBaseline = 'middle';
    const ys = niceStep(vMax - vMin, 3);
    ctx.save(); ctx.beginPath(); ctx.rect(padL, top, w, h); ctx.clip();
    for (let gv = Math.ceil(vMin / ys) * ys; gv <= vMax; gv += ys) { const y = Y(gv); ctx.strokeStyle = Math.abs(gv) < 1e-9 ? LINE : GRID; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(padL, y + 0.5); ctx.lineTo(padL + w, y + 0.5); ctx.stroke(); }
    ctx.restore();
    ctx.textAlign = 'right';
    for (let gv = Math.ceil(vMin / ys) * ys; gv <= vMax; gv += ys) { const y = Y(gv); if (y < top - 1 || y > top + h + 1) continue; ctx.fillStyle = Math.abs(gv) < 1e-9 ? MUTED : MUT2; ctx.fillText(fmtK(gv), padL - 9, y); }
    ctx.save(); ctx.beginPath(); ctx.rect(padL, top, w, h); ctx.clip();
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (const ss of lines) {
      if (ss.vals.length < 2) continue;
      ctx.beginPath(); ss.vals.forEach((p, i) => { const x = X(p.t), y = Y(p.v); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
      ctx.strokeStyle = ss.color; ctx.lineWidth = ss.width; ctx.setLineDash(ss.dash); ctx.globalAlpha = ss.width < 2 ? 0.78 : 0.95; ctx.stroke(); ctx.globalAlpha = 1; ctx.setLineDash([]);
      const last = ss.vals[ss.vals.length - 1], lx = X(last.t), ly = Y(last.v);
      if (lx >= padL - 2 && lx <= padL + w + 2) { ctx.beginPath(); ctx.arc(lx, ly, 3.4, 0, 7); ctx.fillStyle = ss.color; ctx.fill(); ctx.beginPath(); ctx.arc(lx, ly, 3.4, 0, 7); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); }
    }
    ctx.restore();
    // end labels: panes are half-height, so labels that land within 11px of each other are nudged apart
    ctx.font = '600 10.5px ' + MONO; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    const ends: { x: number; y: number; label: string; color: string }[] = [];
    for (const ss of lines) { if (ss.vals.length < 2) continue; const last = ss.vals[ss.vals.length - 1], lx = X(last.t), ly = Y(last.v); if (lx >= padL - 2 && lx <= padL + w + 20 && ly >= top && ly <= top + h) ends.push({ x: Math.min(lx + 4, padL + w + 4), y: ly, label: ss.label, color: ss.color }); }
    ends.sort((a, b) => a.y - b.y);
    for (let i = 1; i < ends.length; i++) if (ends[i].y < ends[i - 1].y + 11) ends[i].y = ends[i - 1].y + 11;
    const over = ends.length ? ends[ends.length - 1].y - (top + h - 5) : 0; if (over > 0) for (const e of ends) e.y -= over;
    for (const e of ends) { ctx.fillStyle = e.color; ctx.fillText(' ' + e.label, e.x, e.y); }
    for (const ss of lines) {
      if (!hi.has(ss.label) || ss.vals.length < 2) continue;
      let peak = ss.vals[0]; for (const p of ss.vals) if (p.v > peak.v) peak = p;
      const py = Y(peak.v), px = X(peak.t);
      ctx.save(); ctx.beginPath(); ctx.rect(padL, top, w, h); ctx.clip();
      ctx.strokeStyle = ss.color; ctx.globalAlpha = 0.5; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(padL, py + 0.5); ctx.lineTo(padL + w, py + 0.5); ctx.stroke();
      ctx.setLineDash([]); ctx.globalAlpha = 1;
      if (px >= padL && px <= padL + w) { ctx.beginPath(); ctx.arc(px, py, 4.2, 0, 7); ctx.fillStyle = ss.color; ctx.fill(); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); }
      ctx.restore();
      ctx.font = '700 10px ' + MONO; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      const tag = '▲ ' + ss.label + ' ' + fmtK(peak.v) + ' @ ' + etTime(peak.t);
      const tw = ctx.measureText(tag).width + 12;
      const tyy = Math.max(top + 8, Math.min(top + h - 8, py - 9));
      ctx.fillStyle = `rgba(${INK_BG_RGB},0.9)`; ctx.fillRect(padL + 3, tyy - 8, tw, 16);
      ctx.fillStyle = ss.color; ctx.fillText(tag, padL + 9, tyy);
    }
  };
  panes.forEach(drawPane);
  if (und && !und.below) panes.forEach((pn, i) => drawUnderlying(ctx, und, X, tMin, tMax, padL, padL + w, pn.top, pn.h, CW, 3, niceStep, i === 0));
  // pane titles at the inline-start above each pane, a thin divider between them, the x-axis once at the bottom
  const [pC, pP] = panes;
  const TITLE = (copy === PANE_COPY.ar ? '700 11px "IBM Plex Sans", system-ui, sans-serif' : '700 10px ' + MONO);
  ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
  ctx.font = TITLE; ctx.fillStyle = MUTED; ctx.fillText(copy.C, padL - 2, padT - 6);
  const cw = ctx.measureText(copy.C).width;
  ctx.font = '600 9px ' + MONO; ctx.fillStyle = MUT2; ctx.fillText(' · ' + unit, padL - 2 + cw, padT - 6);
  const divY = Math.round(pC.top + pC.h + 6) + 0.5;
  ctx.strokeStyle = LINE; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(6, divY); ctx.lineTo(CW - 6, divY); ctx.stroke();
  ctx.font = TITLE; ctx.fillStyle = MUTED; ctx.fillText(copy.P, padL - 2, pP.top - 8);
  const xb = pP.top + pP.h + 13;
  ctx.font = '500 10px ' + MONO; ctx.textAlign = 'center'; ctx.fillStyle = MUT2;
  const xs = niceStep(tMax - tMin, 5);
  for (let gt = Math.ceil(tMin / xs) * xs; gt <= tMax; gt += xs) { if (X(gt) < padL - 1 || X(gt) > padL + w + 1) continue; ctx.fillText(etTime(gt), X(gt), xb); }
  ctx.textAlign = 'left'; ctx.font = '600 9px ' + MONO; ctx.fillText('ET', padL + w + 8, xb);
  // crosshair: one time, values for both panes
  if (hov != null && s[0]) {
    const idx = Math.max(0, Math.min(s[0].vals.length - 1, hov)); const p0 = s[0].vals[idx];
    if (p0) {
      const hx = X(p0.t);
      if (hx >= padL && hx <= padL + w) {
        ctx.strokeStyle = LINE; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
        for (const pn of panes) { ctx.beginPath(); ctx.moveTo(hx + 0.5, pn.top); ctx.lineTo(hx + 0.5, pn.top + pn.h); ctx.stroke(); }
        ctx.setLineDash([]);
        const groups: Record<PaneKey, { c: string; l: string; v: number }[]> = { C: [], P: [] };
        for (const ss of s) {
          const pp = ss.vals[idx]; const pn = panes.find(p => p.key === ss.pane); if (!pp || !pn) continue;
          const py = pn.Y(pp.v);
          if (py >= pn.top && py <= pn.top + pn.h) { ctx.beginPath(); ctx.arc(X(pp.t), py, 3.6, 0, 7); ctx.fillStyle = ss.color; ctx.fill(); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); }
          groups[pn.key].push({ c: ss.color, l: ss.label, v: pp.v });
        }
        if (tip) {
          const rows = (rs: { c: string; l: string; v: number }[]) => rs.sort((a, b) => b.v - a.v).map(rw => `<div class="ft-tr"><span class="ft-lg"><span class="ft-sw" style="background:${rw.c}"></span>${rw.l}</span><span class="ft-val" style="color:${rw.c}">${fmtSign(rw.v)}</span></div>`).join('');
          tip.innerHTML = `<div class="ft-tt">${etTime(p0.t)} ET</div>` + undTipRow(und, p0.t) + `<div class="ft-tp">${copy.tipC}</div>` + rows(groups.C) + `<div class="ft-tp">${copy.tipP}</div>` + rows(groups.P);
          const tw = tip.offsetWidth, sideR = hx > CW - tw - 24; tip.style.left = (sideR ? hx - tw - 14 : hx + 14) + 'px'; tip.style.top = '14px'; tip.style.opacity = '1';
        }
      }
    }
  } else if (tip) tip.style.opacity = '0';
  return { padL, padT, w, h: CH - padT - padB, dom: { tMin, tMax, vMin: 0, vMax: 1 } as Domain, invX, invY: (py: number) => paneAt(panes, py).invY(py), panes };
}

export default function FlowTape({ scannerData }: { scannerData?: any[] }) {
  applyFtPalette();
  const underlyings = useMemo(() => {
    const set = new Set<string>();
    for (const r of scannerData || []) { const u = String((r?.Underlying ?? r?.underlying) || '').toUpperCase().trim(); if (u) set.add(u); }
    const arr = Array.from(set).sort();
    return arr.length ? arr : ['SPXW', 'SPY', 'QQQ'];
  }, [scannerData]);

  const [underlying, setUnderlying] = useState(() => takeEdgeSymbol() || (typeof window !== 'undefined' && localStorage.getItem('ft_und')) || '');
  const [expiries, setExpiries] = useState<string[]>([]);
  const [expiry, setExpiry] = useState<string>('');
  const [sessions, setSessions] = useState<string[]>([]);
  const [session, setSession] = useState<string>('');
  const [measure, setMeasure] = useState<Measure>('delta');
  const [basis, setBasis] = useState<Basis>('accumulated');
  const [splitCP, setSplitCP] = useState(false);
  const [breakdown, setBreakdown] = useState<Breakdown>('net');
  const [layout, setLayout] = useState<Layout>(() => { try { return localStorage.getItem('ft_layout') === 'two' ? 'two' : 'one'; } catch { return 'one'; } });
  useEffect(() => { try { localStorage.setItem('ft_layout', layout); } catch { /* noop */ } }, [layout]);
  const isArabic = useContext(LanguageContext)?.language === 'ar';
  const paneCopy = PANE_COPY[isArabic ? 'ar' : 'en'];
  const undCopy = UND_COPY[isArabic ? 'ar' : 'en'];
  const [highs, setHighs] = useState<Set<string>>(new Set());  // series labels whose high marker is shown
  const [ladder, setLadder] = useState<LadderStrike[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [focus, setFocus] = useState<string | null>(null);
  const [series, setSeries] = useState<Record<string, FlowPoint[]>>({});
  const [tsdbDown, setTsdbDown] = useState(false);
  const [lastTick, setLastTick] = useState<number>(0);
  const [showUnd, setShowUnd] = useState<boolean>(() => { try { return localStorage.getItem(UND_KEY) !== '0'; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem(UND_KEY, showUnd ? '1' : '0'); } catch { /* noop */ } }, [showUnd]);
  const [undBars, setUndBars] = useState<UndBar[]>([]);
  const [undBelow, setUndBelow] = useState<boolean>(() => { try { return localStorage.getItem(UND_LAYOUT_KEY) === 'below'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(UND_LAYOUT_KEY, undBelow ? 'below' : 'overlay'); } catch { /* noop */ } }, [undBelow]);

  const isLive = sessions.length > 0 && session === sessions[0];
  const mode = basis;

  useEffect(() => { if (!underlying && underlyings.length) setUnderlying(underlyings.includes('SPXW') ? 'SPXW' : underlyings[0]); }, [underlyings, underlying]);
  useEffect(() => { if (underlying) try { localStorage.setItem('ft_und', underlying); } catch { /* noop */ } }, [underlying]);

  // expiries
  useEffect(() => {
    if (!underlying) return; let dead = false;
    (async () => {
      const d = await jget(`/api/legacy-rtl/contracts?underlying=${encodeURIComponent(underlying)}&days=14`);
      if (dead) return;
      const exps = Array.from(new Set((d?.contracts || []).map((c: any) => String(c.expiry || c.expiry_date || '')).filter(Boolean))).sort() as string[];
      // Default = the nearest expiry that has not passed yet (0DTE on a trading day); after
      // the close / on a weekend that is none, so fall back to the latest one — the earliest
      // in the list is usually the prior day's, which only carries pre-market leftovers.
      const todayEt = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      const dflt = exps.find(x => x >= todayEt) || exps[exps.length - 1] || '';
      setExpiries(exps); setExpiry(prev => (prev && exps.includes(prev)) ? prev : dflt);
    })();
    return () => { dead = true; };
  }, [underlying]);

  // sessions (trading days) → default to the most recent (opens last day's RTH)
  useEffect(() => {
    if (!underlying) return; let dead = false;
    (async () => {
      const d = await jget(`/api/rtl-flow/sessions?underlying=${encodeURIComponent(underlying)}${expiry ? `&expiry=${encodeURIComponent(expiry)}` : ''}&days=25`);
      if (dead) return;
      const ss: string[] = d?.sessions || [];
      setSessions(ss);
      setSession(prev => (prev && ss.includes(prev)) ? prev : (ss[0] || ''));
    })();
    return () => { dead = true; };
  }, [underlying, expiry]);

  const loadLadder = useCallback(async () => {
    if (!underlying || !session) return;
    const d = await jget(`/api/rtl-flow/ladder?underlying=${encodeURIComponent(underlying)}${expiry ? `&expiry=${encodeURIComponent(expiry)}` : ''}&session=${session}`);
    if (d?.success) { setLadder(d.strikes || []); setTsdbDown(!(d.strikes || []).length && d.timeseriesdb_available === false); }
    else setTsdbDown(true);
  }, [underlying, expiry, session]);

  const loadSeries = useCallback(async () => {
    if (!underlying || !session || selected.length === 0) { setSeries({}); return; }
    const d = await jget(`/api/rtl-flow/series?underlying=${encodeURIComponent(underlying)}&strikes=${selected.join(',')}${expiry ? `&expiry=${encodeURIComponent(expiry)}` : ''}&session=${session}&mode=${mode}&measure=${measure}`);
    if (d?.success) { setSeries(d.series || {}); setLastTick(Date.now()); }
  }, [underlying, expiry, session, selected, mode, measure]);

  // Underlying bars for the session (1-minute; index roots come back on their native scale, 5-minute).
  // Refreshed from the same live loop as the series, but at most once every 30 s: the bars are 1-minute.
  const undSym = useMemo(() => getChartSymbol(underlying || ''), [underlying]);
  const undLabel = useMemo(() => { const r = resolveSymbol(underlying || ''); return (r?.chartSymbol ? r.priceSource : r?.displaySymbol) || underlying; }, [underlying]);
  const undScale = /^XSP$/i.test(underlying) ? 0.1 : 1;   // XSP is one-tenth of the S&P 500 index
  const undFetchRef = useRef<{ key: string; at: number }>({ key: '', at: 0 });
  const loadUnd = useCallback(async (force = false) => {
    if (!showUnd || !undSym || !session) { setUndBars([]); return; }
    const key = `${undSym}|${session}`; const now = Date.now();
    if (!force && undFetchRef.current.key === key && now - undFetchRef.current.at < 30_000) return;
    undFetchRef.current = { key, at: now };
    const d = await jget(`/api/historical/equity-bars?symbol=${encodeURIComponent(undSym)}&interval=1m&from=${session}&to=${session}`);
    if (undFetchRef.current.key !== key) return;            // underlying/session changed meanwhile
    const bars: UndBar[] = (d?.bars || []).map((b: any) => ({ t: typeof b.t === 'number' ? b.t : new Date(b.t).getTime(), c: (Number(b.c) || 0) * undScale }))
      .filter((b: UndBar) => b.c > 0 && isFinite(b.t)).sort((a: UndBar, b: UndBar) => a.t - b.t);
    if (bars.length || force) setUndBars(bars);               // a blip keeps the line already drawn
  }, [showUnd, undSym, session, undScale]);
  useEffect(() => { setUndBars([]); loadUnd(true); }, [loadUnd]);

  useEffect(() => { loadLadder(); }, [loadLadder]);
  useEffect(() => { loadSeries(); }, [loadSeries]);

  // live polling only for the most-recent session
  useEffect(() => {
    if (!isLive) return;
    const id = setInterval(() => { loadLadder(); loadSeries(); loadUnd(); }, POLL_MS);
    return () => clearInterval(id);
  }, [isLive, loadLadder, loadSeries, loadUnd]);

  // default selection when ladder first loads for an underlying/expiry
  const seededRef = useRef('');
  useEffect(() => {
    const k = `${underlying}|${expiry}`;
    if (seededRef.current === k || ladder.length === 0) return;
    seededRef.current = k;
    const byVol = [...ladder].sort((a, b) => (b.cVol + b.pVol) - (a.cVol + a.pVol));
    const picks = byVol.slice(0, 3).map(s => s.key);
    setSelected(picks); setFocus(picks[0] ?? null);
  }, [ladder, underlying, expiry]);

  const toggleStrike = (kk: string) => {
    setSelected(prev => {
      const i = prev.indexOf(kk);
      if (i >= 0) { const n = prev.slice(); n.splice(i, 1); setFocus(f => (f === kk ? (n[n.length - 1] ?? null) : f)); return n; }
      setFocus(kk); return [...prev, kk];
    });
  };
  // Strike Movers panel: add-and-focus (never removes) / plot only this strike
  const pickStrike = useCallback((kk: string) => { setSelected(prev => (prev.includes(kk) ? prev : [...prev, kk])); setFocus(kk); }, []);
  const pickOnly = useCallback((kk: string) => { setSelected([kk]); setFocus(kk); }, []);
  const pickMany = useCallback((keys: string[]) => { if (keys.length) { setSelected(keys); setFocus(keys[0]); } }, []);
  const shiftDay = (delta: number) => {
    const i = sessions.indexOf(session); if (i < 0) return;
    const n = sessions[i + delta]; if (n) setSession(n);
  };

  const chartSeries: ChartSeries[] = useMemo(() => {
    const out: ChartSeries[] = [];
    selected.forEach((k, idx) => {
      const pts = series[k]; if (!pts) return; const col = SERIES[idx % SERIES.length];
      if (layout === 'two') {
        // Two panes: calls in the upper pane, puts in the lower one (same strike colour in both).
        // net/callput → call net vs put net; buysell → call buys/sells vs put buys/sells.
        if (breakdown === 'buysell') {
          out.push({ label: k + '·C Buy', color: col, dash: [], width: 1.8, pane: 'C', vals: pts.map(p => ({ t: p.t, v: p.cBuy })) });
          out.push({ label: k + '·C Sell', color: col, dash: [5, 4], width: 1.8, pane: 'C', vals: pts.map(p => ({ t: p.t, v: p.cSell })) });
          out.push({ label: k + '·P Buy', color: col, dash: [], width: 1.8, pane: 'P', vals: pts.map(p => ({ t: p.t, v: p.pBuy })) });
          out.push({ label: k + '·P Sell', color: col, dash: [5, 4], width: 1.8, pane: 'P', vals: pts.map(p => ({ t: p.t, v: p.pSell })) });
        } else {
          out.push({ label: k + '·C', color: col, dash: [], width: 2, pane: 'C', vals: pts.map(p => ({ t: p.t, v: p.cNet })) });
          out.push({ label: k + '·P', color: col, dash: [], width: 2, pane: 'P', vals: pts.map(p => ({ t: p.t, v: p.pNet })) });
        }
        return;
      }
      if (breakdown === 'callput') {
        // Call vs Put: solid = call, dashed = put (same strike colour)
        out.push({ label: k + '·C', color: col, dash: [], width: 1.8, vals: pts.map(p => ({ t: p.t, v: p.cNet })) });
        out.push({ label: k + '·P', color: col, dash: [5, 4], width: 1.8, vals: pts.map(p => ({ t: p.t, v: p.pNet })) });
      } else if (breakdown === 'buysell') {
        // Buys vs Sells: solid = buys (@ask), dashed = sells (@bid)
        if (splitCP) {
          // split per side — calls bold, puts faint
          out.push({ label: k + '·C Buy', color: col, dash: [], width: 2, vals: pts.map(p => ({ t: p.t, v: p.cBuy })) });
          out.push({ label: k + '·C Sell', color: col, dash: [5, 4], width: 2, vals: pts.map(p => ({ t: p.t, v: p.cSell })) });
          out.push({ label: k + '·P Buy', color: col, dash: [], width: 1.2, vals: pts.map(p => ({ t: p.t, v: p.pBuy })) });
          out.push({ label: k + '·P Sell', color: col, dash: [5, 4], width: 1.2, vals: pts.map(p => ({ t: p.t, v: p.pSell })) });
        } else {
          out.push({ label: k + '·Buy', color: col, dash: [], width: 1.8, vals: pts.map(p => ({ t: p.t, v: p.buy })) });
          out.push({ label: k + '·Sell', color: col, dash: [5, 4], width: 1.8, vals: pts.map(p => ({ t: p.t, v: p.sell })) });
        }
      } else {
        // Net flow (call net − put net); optional Split C/P overlay
        out.push({ label: k, color: col, dash: [], width: 2, vals: pts.map(p => ({ t: p.t, v: p.net })) });
        if (splitCP) {
          out.push({ label: k + '·C', color: col, dash: [], width: 1.4, vals: pts.map(p => ({ t: p.t, v: p.cNet })) });
          out.push({ label: k + '·P', color: col, dash: [5, 4], width: 1.4, vals: pts.map(p => ({ t: p.t, v: p.pNet })) });
        }
      }
    });
    return out;
  }, [series, selected, splitCP, breakdown, layout]);

  // ── canvas + interaction ───────────────────────────────
  const cvRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<Domain | null>(null);        // user zoom/pan; null = auto-fit
  const hoverRef = useRef<number | null>(null);
  const dragRef = useRef<{ x: number; y: number; dom: Domain; pane?: PaneKey; py?: PaneY; und?: boolean } | null>(null);
  const plotRef = useRef<any>(null);
  const paneYRef = useRef<Partial<Record<PaneKey, PaneY>>>({});   // Two panes: per-pane y zoom/pan; {} = auto-fit
  const seriesRef = useRef<ChartSeries[]>([]);
  seriesRef.current = chartSeries;
  const highsRef = useRef<Set<string>>(highs);
  highsRef.current = highs;
  const undRef = useRef<UndOpts | null>(null);
  undRef.current = showUnd && undBars.length > 1 ? { bars: undBars, sym: undLabel, name: undCopy.name, below: undBelow } : null;
  const toggleHigh = useCallback((label: string) => {
    setHighs(prev => { const n = new Set(prev); n.has(label) ? n.delete(label) : n.add(label); return n; });
  }, []);
  // drop high markers for lines that no longer exist
  useEffect(() => {
    setHighs(prev => {
      const live = new Set(chartSeries.map(s => s.label));
      let changed = false; const n = new Set<string>();
      prev.forEach(l => { if (live.has(l)) n.add(l); else changed = true; });
      return changed ? n : prev;
    });
  }, [chartSeries]);

  // new data shape → reset the viewport to auto-fit
  useEffect(() => { viewRef.current = null; paneYRef.current = {}; }, [session, measure, basis, underlying, expiry, breakdown, layout]);
  // Trades have no buy/sell aggressor split — fall back to Net if Buy/Sell was active
  useEffect(() => { if (measure === 'trades' && breakdown === 'buysell') setBreakdown('net'); }, [measure, breakdown]);

  const niceStep = (rng: number, target: number) => { const raw = rng / target, mag = Math.pow(10, Math.floor(Math.log10(raw) || 0)); const n = raw / mag; return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * mag; };

  const draw = useCallback(() => {
    const cv = cvRef.current, wrap = wrapRef.current; if (!cv || !wrap) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const r = wrap.getBoundingClientRect(); const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const CW = r.width, CH = r.height;
    if (cv.width !== Math.round(CW * dpr) || cv.height !== Math.round(CH * dpr)) { cv.width = Math.round(CW * dpr); cv.height = Math.round(CH * dpr); }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, CW, CH);
    const s = seriesRef.current; const active = s.length > 0 && s.some(x => x.vals.length > 1);
    if (!active) { plotRef.current = null; return; }
    const und = undRef.current; const below = !!und && und.below;
    // "Below": the flow chart keeps the top of the canvas and the price pane takes ~35% of its height
    // underneath. One canvas, so both share the exact time→x mapping, crosshair and zoom/pan.
    const CHf = below ? Math.round(CH / (1 + UND_BELOW_RATIO)) : CH;
    const drawBelow = () => {
      const plot = plotRef.current; if (!plot || !und) return;
      const { padL: pl, w: pw } = plot; const { tMin: t0, tMax: t1 } = plot.dom;
      const Xb = (t: number) => pl + (t - t0) / (t1 - t0) * pw;
      const top = CHf + 16, ph = CH - top - 22; if (ph < 28) return;
      plot.undTop = CHf;
      const MONO = CV_MONO;
      ctx.strokeStyle = LINE; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(6, CHf + 1.5); ctx.lineTo(CW - 6, CHf + 1.5); ctx.stroke();
      const PY = drawUnderlying(ctx, und, Xb, t0, t1, pl, pl + pw, top, ph, CW, 4, niceStep, false, true);
      ctx.font = (isArabic ? '700 11px "IBM Plex Sans", system-ui, sans-serif' : '700 10px ' + MONO); ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillStyle = `rgba(${INK_RGB},0.75)`; ctx.fillText(und.name + ' · ' + und.sym, pl - 2, top - 7);
      ctx.font = '500 10px ' + MONO; ctx.textAlign = 'center'; ctx.fillStyle = MUT2;
      const xs = niceStep(t1 - t0, 5);
      for (let gt = Math.ceil(t0 / xs) * xs; gt <= t1; gt += xs) { if (Xb(gt) < pl - 1 || Xb(gt) > pl + pw + 1) continue; ctx.fillText(etTime(gt), Xb(gt), top + ph + 12); }
      ctx.textAlign = 'left'; ctx.font = '600 9px ' + MONO; ctx.fillText('ET', pl + pw + 8, top + ph + 12);
      const hv = hoverRef.current; const base = s[0]?.vals || [];
      if (hv != null && base.length) {
        const p0 = base[Math.max(0, Math.min(base.length - 1, hv))]; const hx = Xb(p0.t);
        if (hx >= pl && hx <= pl + pw) {
          ctx.strokeStyle = LINE; ctx.lineWidth = 1; ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(hx + 0.5, top); ctx.lineTo(hx + 0.5, top + ph); ctx.stroke(); ctx.setLineDash([]);
          const b = undAt(und.bars, p0.t);
          if (b && PY) { const y = PY(b.c); if (y >= top && y <= top + ph) { ctx.beginPath(); ctx.arc(Xb(b.t), y, 3.4, 0, 7); ctx.fillStyle = UND_COLOR; ctx.fill(); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); } }
        }
      }
    };
    if (layout === 'two') {
      plotRef.current = drawTwoPanes(ctx, s, CW, CHf, { view: viewRef.current, paneY: paneYRef.current, hov: hoverRef.current, hi: highsRef.current, tip: tipRef.current, unit: MEASURE_UNIT[measure] + (basis === '5min' ? ' · 5m' : ''), copy: paneCopy, niceStep, und });
      if (below) drawBelow();
      return;
    }
    const padL = 58, padR = 66 + (und && !below ? UND_PAD : 0), padT = 16, padB = 26, w = CW - padL - padR, h = CHf - padT - padB;
    let dom: Domain;
    if (viewRef.current) dom = viewRef.current;
    else {
      let tMin = Infinity, tMax = -Infinity, vMin = Infinity, vMax = -Infinity;
      for (const ss of s) for (const p of ss.vals) { if (p.t < tMin) tMin = p.t; if (p.t > tMax) tMax = p.t; if (p.v < vMin) vMin = p.v; if (p.v > vMax) vMax = p.v; }
      if (tMin === tMax) tMax = tMin + 1; if (vMin === vMax) { vMin -= 1; vMax += 1; }
      const pad = (vMax - vMin) * 0.12; vMin -= pad; vMax += pad; if (vMin > 0) vMin = 0; if (vMax < 0) vMax = 0;
      dom = { tMin, tMax, vMin, vMax };
    }
    const { tMin, tMax, vMin, vMax } = dom;
    const X = (t: number) => padL + (t - tMin) / (tMax - tMin) * w;
    const Y = (v: number) => padT + (1 - (v - vMin) / (vMax - vMin)) * h;
    plotRef.current = { padL, padT, w, h, dom, invX: (px: number) => tMin + (px - padL) / w * (tMax - tMin), invY: (py: number) => vMax - (py - padT) / h * (vMax - vMin) };
    // grid + y labels
    ctx.font = '500 10px ' + CV_MONO; ctx.textBaseline = 'middle';
    const ys = niceStep(vMax - vMin, 4);
    ctx.save(); ctx.beginPath(); ctx.rect(padL, padT, w, h); ctx.clip();
    for (let gv = Math.ceil(vMin / ys) * ys; gv <= vMax; gv += ys) { const y = Y(gv); ctx.strokeStyle = Math.abs(gv) < 1e-9 ? LINE : GRID; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(padL, y + 0.5); ctx.lineTo(padL + w, y + 0.5); ctx.stroke(); }
    ctx.restore();
    ctx.textAlign = 'right';
    for (let gv = Math.ceil(vMin / ys) * ys; gv <= vMax; gv += ys) { const y = Y(gv); ctx.fillStyle = Math.abs(gv) < 1e-9 ? MUTED : MUT2; ctx.fillText(fmtK(gv), padL - 9, y); }
    ctx.textAlign = 'center'; ctx.fillStyle = MUT2;
    const xs = niceStep(tMax - tMin, 5);
    for (let gt = Math.ceil(tMin / xs) * xs; gt <= tMax; gt += xs) { if (X(gt) < padL - 1 || X(gt) > padL + w + 1) continue; ctx.fillText(etTime(gt), X(gt), padT + h + 13); }
    ctx.textAlign = 'left'; ctx.fillStyle = MUT2; ctx.font = '600 9px ' + CV_MONO;
    ctx.fillText(MEASURE_UNIT[measure] + (basis === '5min' ? ' · 5m' : ''), padL - 2, padT - 6);
    ctx.fillText('ET', padL + w + 8, padT + h + 13);
    // underlying price first, so the strike lines sit on top of it
    if (und && !below) drawUnderlying(ctx, und, X, tMin, tMax, padL, padL + w, padT, h, CW, 4, niceStep, true);
    // lines (clipped to plot)
    ctx.save(); ctx.beginPath(); ctx.rect(padL, padT, w, h); ctx.clip();
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (const ss of s) {
      if (ss.vals.length < 2) continue;
      ctx.beginPath(); ss.vals.forEach((p, i) => { const x = X(p.t), y = Y(p.v); i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
      ctx.strokeStyle = ss.color; ctx.lineWidth = ss.width; ctx.setLineDash(ss.dash); ctx.globalAlpha = ss.width < 2 ? 0.78 : 0.95; ctx.stroke(); ctx.globalAlpha = 1; ctx.setLineDash([]);
      const last = ss.vals[ss.vals.length - 1], lx = X(last.t), ly = Y(last.v);
      if (lx >= padL - 2 && lx <= padL + w + 2) { ctx.beginPath(); ctx.arc(lx, ly, 3.4, 0, 7); ctx.fillStyle = ss.color; ctx.fill(); ctx.beginPath(); ctx.arc(lx, ly, 3.4, 0, 7); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); }
    }
    ctx.restore();
    // end labels outside clip
    ctx.font = '600 10.5px ' + CV_MONO; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    for (const ss of s) { if (ss.vals.length < 2) continue; const last = ss.vals[ss.vals.length - 1], lx = X(last.t), ly = Y(last.v); if (lx >= padL - 2 && lx <= padL + w + 20 && ly >= padT && ly <= padT + h) { ctx.fillStyle = ss.color; ctx.fillText(' ' + ss.label, Math.min(lx + 4, padL + w + 4), ly); } }
    // per-line high markers (toggled from the legend)
    const hi = highsRef.current;
    if (hi && hi.size) {
      for (const ss of s) {
        if (!hi.has(ss.label) || ss.vals.length < 2) continue;
        let peak = ss.vals[0]; for (const p of ss.vals) if (p.v > peak.v) peak = p;
        const py = Y(peak.v), px = X(peak.t);
        ctx.save(); ctx.beginPath(); ctx.rect(padL, padT, w, h); ctx.clip();
        ctx.strokeStyle = ss.color; ctx.globalAlpha = 0.5; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(padL, py + 0.5); ctx.lineTo(padL + w, py + 0.5); ctx.stroke();
        ctx.setLineDash([]); ctx.globalAlpha = 1;
        if (px >= padL && px <= padL + w) { ctx.beginPath(); ctx.arc(px, py, 4.2, 0, 7); ctx.fillStyle = ss.color; ctx.fill(); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); }
        ctx.restore();
        // high tag pinned to the left edge, unclipped
        ctx.font = '700 10px ' + CV_MONO; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
        const tag = '▲ ' + ss.label + ' ' + fmtK(peak.v) + ' @ ' + etTime(peak.t);
        const tw = ctx.measureText(tag).width + 12;
        const tyy = Math.max(padT + 8, Math.min(padT + h - 8, py - 9));
        ctx.fillStyle = `rgba(${INK_BG_RGB},0.9)`; ctx.fillRect(padL + 3, tyy - 8, tw, 16);
        ctx.fillStyle = ss.color; ctx.fillText(tag, padL + 9, tyy);
      }
    }
    // crosshair
    const hov = hoverRef.current;
    if (hov != null && s[0]) {
      const idx = Math.max(0, Math.min(s[0].vals.length - 1, hov)); const p0 = s[0].vals[idx];
      if (p0) {
        const hx = X(p0.t);
        if (hx >= padL && hx <= padL + w) {
          ctx.strokeStyle = LINE; ctx.lineWidth = 1; ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(hx + 0.5, padT); ctx.lineTo(hx + 0.5, padT + h); ctx.stroke(); ctx.setLineDash([]);
          const rows: { c: string; l: string; v: number }[] = [];
          for (const ss of s) { const pp = ss.vals[idx]; if (pp) { const py = Y(pp.v); if (py >= padT && py <= padT + h) { ctx.beginPath(); ctx.arc(X(pp.t), py, 3.6, 0, 7); ctx.fillStyle = ss.color; ctx.fill(); ctx.strokeStyle = INK_BG; ctx.lineWidth = 1.5; ctx.stroke(); } rows.push({ c: ss.color, l: ss.label, v: pp.v }); } }
          const tip = tipRef.current;
          if (tip) { rows.sort((a, b) => b.v - a.v); tip.innerHTML = `<div class="ft-tt">${etTime(p0.t)} ET</div>` + undTipRow(und, p0.t) + rows.map(rw => `<div class="ft-tr"><span class="ft-lg"><span class="ft-sw" style="background:${rw.c}"></span>${rw.l}</span><span class="ft-val" style="color:${rw.c}">${fmtSign(rw.v)}</span></div>`).join(''); const tw = tip.offsetWidth, sideR = hx > CW - tw - 24; tip.style.left = (sideR ? hx - tw - 14 : hx + 14) + 'px'; tip.style.top = '14px'; tip.style.opacity = '1'; }
        }
      }
    } else if (tipRef.current) tipRef.current.style.opacity = '0';
    if (below) drawBelow();
  }, [measure, basis, layout, paneCopy, isArabic]);

  useEffect(() => { draw(); }, [chartSeries, highs, undBars, showUnd, undBelow, draw]);
  useEffect(() => { const wrap = wrapRef.current; if (!wrap) return; const ro = new ResizeObserver(() => draw()); ro.observe(wrap); return () => ro.disconnect(); }, [draw]);

  // wheel zoom (native listener so preventDefault works)
  useEffect(() => {
    const cv = cvRef.current; if (!cv) return;
    const onWheel = (e: WheelEvent) => {
      const plot = plotRef.current; if (!plot) return; e.preventDefault();
      const r = cv.getBoundingClientRect(); const mx = e.clientX - r.left, my = e.clientY - r.top;
      const f = e.deltaY < 0 ? 0.82 : 1.22; const d = { ...plot.dom } as Domain;
      const inUnd = plot.undTop != null && my > plot.undTop;   // price pane below: time zoom only
      if (e.shiftKey && plot.panes && !inUnd) { const pn = paneAt(plot.panes, my); const vAt = pn.invY(my); paneYRef.current = { ...paneYRef.current, [pn.key]: { vMin: vAt - (vAt - pn.vMin) * f, vMax: vAt + (pn.vMax - vAt) * f } }; draw(); return; }  // Two panes: zoom the pane under the cursor
      if (e.shiftKey && !inUnd) { const vAt = plot.invY(my); d.vMin = vAt - (vAt - d.vMin) * f; d.vMax = vAt + (d.vMax - vAt) * f; }
      else { const tAt = plot.invX(mx); d.tMin = tAt - (tAt - d.tMin) * f; d.tMax = tAt + (d.tMax - tAt) * f; }
      viewRef.current = d; draw();
    };
    cv.addEventListener('wheel', onWheel, { passive: false });
    return () => cv.removeEventListener('wheel', onWheel);
  }, [draw]);

  const onDown = (e: React.MouseEvent) => { const plot = plotRef.current; if (!plot) return; const r = (e.target as HTMLElement).getBoundingClientRect(); const y0 = e.clientY - r.top; const inUnd = plot.undTop != null && y0 > plot.undTop; const pn = plot.panes && !inUnd ? paneAt(plot.panes, y0) : null; dragRef.current = { x: e.clientX - r.left, y: y0, und: inUnd, dom: { ...plot.dom }, pane: pn?.key, py: pn ? { vMin: pn.vMin, vMax: pn.vMax } : undefined }; hoverRef.current = null; };
  const onMove = (e: React.MouseEvent) => {
    const cv = cvRef.current, plot = plotRef.current; if (!cv || !plot) return;
    const r = cv.getBoundingClientRect(); const x = e.clientX - r.left, y = e.clientY - r.top;
    if (dragRef.current) {
      const dom = dragRef.current.dom; const dxpx = x - dragRef.current.x, dypx = y - dragRef.current.y;
      if (plot.panes) {  // Two panes: time pans both; the vertical part pans only the pane the drag started in
        const dT = -dxpx / plot.w * (dom.tMax - dom.tMin); viewRef.current = { ...dom, tMin: dom.tMin + dT, tMax: dom.tMax + dT };
        const dg = dragRef.current; const pn = dg.pane ? plot.panes.find((p: PaneRect) => p.key === dg.pane) : null;
        if (pn && dg.py) { const dV = dypx / pn.h * (dg.py.vMax - dg.py.vMin); paneYRef.current = { ...paneYRef.current, [pn.key]: { vMin: dg.py.vMin + dV, vMax: dg.py.vMax + dV } }; }
        draw(); return;
      }
      const dT = -dxpx / plot.w * (dom.tMax - dom.tMin), dV = dragRef.current.und ? 0 : dypx / plot.h * (dom.vMax - dom.vMin);
      viewRef.current = { tMin: dom.tMin + dT, tMax: dom.tMax + dT, vMin: dom.vMin + dV, vMax: dom.vMax + dV }; draw(); return;
    }
    if (x < plot.padL || x > plot.padL + plot.w) { hoverRef.current = null; draw(); return; }
    const t = plot.invX(x); const base = seriesRef.current[0]?.vals || []; let best = 0, bd = Infinity;
    base.forEach((p, i) => { const dd = Math.abs(p.t - t); if (dd < bd) { bd = dd; best = i; } });
    hoverRef.current = best; draw();
  };
  const onUp = () => { dragRef.current = null; };
  const onLeave = () => { dragRef.current = null; hoverRef.current = null; draw(); };
  const resetView = () => { viewRef.current = null; paneYRef.current = {}; draw(); };

  const focusK = focus ?? selected[selected.length - 1] ?? null;
  const focusStrike = focusK ? ladder.find(l => l.key === focusK) : null;
  const atmGuess = ladder.length ? ladder[Math.floor(ladder.length / 2)].strike : 0;
  const breakdownName = breakdown === 'callput' ? ' · Call/Put' : breakdown === 'buysell' ? ' · Buy/Sell' : '';
  const metricName = MEASURE_NAME[measure] + (basis === '5min' ? ' · 5-min' : ' · accumulated') + breakdownName;
  const dayIdx = sessions.indexOf(session);

  return (
    <div className="ft-root">
      <style>{FT_CSS}</style>
      <div className="ft-top">
        <div className="ft-brand"><b>FLOW<span className="ft-accent">·</span>TAPE</b><small>RTL intraday order-flow</small></div>
        <div className="ft-field"><span className="ft-lbl">Underlying</span>
          <select className="ft-pick" value={underlying} onChange={e => { setSelected([]); setFocus(null); seededRef.current = ''; setUnderlying(e.target.value); }}>
            {(underlying && !underlyings.includes(underlying) ? [underlying, ...underlyings] : underlyings).map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div className="ft-field"><span className="ft-lbl">Expiry</span>
          <select className="ft-pick" value={expiry} onChange={e => { setSelected([]); setFocus(null); seededRef.current = ''; setExpiry(e.target.value); }}>
            {expiries.length === 0 && <option value="">all</option>}
            {expiries.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div className="ft-grow" />
        <div className="ft-day">
          <button className="ft-nav" title="Older session" disabled={dayIdx < 0 || dayIdx >= sessions.length - 1} onClick={() => shiftDay(1)}>◀</button>
          <select className="ft-pick ft-daysel" value={session} onChange={e => setSession(e.target.value)}>
            {sessions.length === 0 && <option value="">—</option>}
            {sessions.map((s, i) => <option key={s} value={s}>{dayLabel(s)}{i === 0 ? ' · latest' : ''}</option>)}
          </select>
          <button className="ft-nav" title="Newer session" disabled={dayIdx <= 0} onClick={() => shiftDay(-1)}>▶</button>
        </div>
        <div className="ft-live"><span className={'ft-dot' + (isLive ? '' : ' paused')} /><span>{isLive ? 'live' : 'history'}{lastTick ? ` · ${new Date(lastTick).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</span></div>
      </div>

      <div className="ft-body">
        <aside className="ft-rail">
          <div className="ft-rail-head"><span className="ft-lbl">Strikes</span><span className="ft-hint">click to plot · multi</span></div>
          <div className="ft-ladder">
            {tsdbDown && <div className="ft-note">{isDesignSystemOn() ? 'History store unavailable. The ladder fills in once it is back.' : 'History store unavailable. Check QuestDB.'}</div>}
            {!tsdbDown && ladder.length === 0 && <div className="ft-note">No RTL data for {underlying}{expiry ? ` ${expiry}` : ''} on {session ? dayLabel(session) : 'this session'}.{isLive ? ' (Market may not be open yet.)' : ''}</div>}
            {ladder.map((row) => {
              const selIdx = selected.indexOf(row.key); const tot = Math.max(1, row.cVol + row.pVol); const isAtm = Math.abs(row.strike - atmGuess) < 1e-9;
              return (
                <div key={row.key} className={'ft-srow' + (selIdx >= 0 ? ' sel' : '') + (isAtm ? ' atm' : '')}
                  style={selIdx >= 0 ? ({ ['--sw' as any]: SERIES[selIdx % SERIES.length] }) : undefined}
                  onClick={() => toggleStrike(row.key)}>
                  <span className="ft-swatch" /><div className="ft-k">{row.key}</div>
                  <div className="ft-split" title="call vs put activity"><div className="ft-c" style={{ width: (row.cVol / tot * 100) + '%' }} /><div className="ft-p" style={{ width: (row.pVol / tot * 100) + '%' }} /></div>
                  <div className={'ft-snet ' + cls(row.net)}>{fmtSign(row.net)}</div>
                </div>
              );
            })}
          </div>
        </aside>

        <section className="ft-main">
          <div className="ft-tabs">
            <div className="ft-seg" title="Measure">{MEASURES.map(([m, l]) => <button key={m} className={m === measure ? 'on' : ''} onClick={() => setMeasure(m)}>{l}</button>)}</div>
            <div className="ft-seg" title="Accumulated (session running total) vs 5-min (per-snapshot rate)">
              <button className={basis === 'accumulated' ? 'on' : ''} onClick={() => setBasis('accumulated')}>Accum</button>
              <button className={basis === '5min' ? 'on' : ''} onClick={() => setBasis('5min')}>5-min</button>
            </div>
            <div className="ft-seg" title="Breakdown — Net (call net − put net), Call vs Put, or Buys vs Sells">
              <button className={breakdown === 'net' ? 'on' : ''} onClick={() => setBreakdown('net')}>Net</button>
              <button className={breakdown === 'callput' ? 'on' : ''} onClick={() => setBreakdown('callput')}>Call/Put</button>
              <button className={breakdown === 'buysell' ? 'on' : ''} disabled={measure === 'trades'} title={measure === 'trades' ? 'Trades (trade_count) have no buy/sell aggressor split' : 'Buys (@ask) vs Sells (@bid)'} onClick={() => setBreakdown('buysell')}>Buy/Sell</button>
            </div>
            <div className="ft-seg" title={paneCopy.hint}>
              <button className={layout === 'one' ? 'on' : ''} onClick={() => setLayout('one')}>{paneCopy.one}</button>
              <button className={layout === 'two' ? 'on' : ''} onClick={() => setLayout('two')}>{paneCopy.two}</button>
            </div>
            {layout === 'one' && (breakdown === 'net' || breakdown === 'buysell') && (
              <label className="ft-check" title={breakdown === 'buysell' ? "Split buy/sell into call (bold) and put (faint) lines" : "Also draw each strike's call (solid) and put (dashed) lines"}>
                <input type="checkbox" checked={splitCP} onChange={e => setSplitCP(e.target.checked)} />
                <span>Split C/P</span>
              </label>
            )}
            <label className="ft-check ft-undchk" title={undCopy.hint}>
              <input type="checkbox" checked={showUnd} onChange={e => setShowUnd(e.target.checked)} />
              <span className="ft-undsw" /><span>{undCopy.name}</span>
            </label>
            {showUnd && (
              <div className="ft-seg" title={undCopy.layHint}>
                <button className={!undBelow ? 'on' : ''} onClick={() => setUndBelow(false)}>{undCopy.over}</button>
                <button className={undBelow ? 'on' : ''} onClick={() => setUndBelow(true)}>{undCopy.below}</button>
              </div>
            )}
            <button className="ft-btn" onClick={resetView} title="Reset zoom (or double-click chart)">Fit</button>
            <button className="ft-btn" onClick={() => { setSelected([]); setFocus(null); setSeries({}); }}>Clear</button>
            <span className="ft-metricname">Plotting <b>{metricName}</b> · {chartSeries.length} lines · <span className="ft-zoomhint">scroll = zoom · shift+scroll = price · drag = pan</span></span>
          </div>
          <div className={'ft-chartwrap' + (layout === 'two' ? ' ft-two' : '')} ref={wrapRef}>
            <canvas ref={cvRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onLeave} onDoubleClick={resetView} />
            <div className="ft-tip" ref={tipRef} />
            {chartSeries.length === 0 && (isDesignSystemOn()
              ? <div className="ft-empty"><ToolState kind="empty" title={`Select one or more strikes to plot their intraday flow for ${session ? dayLabel(session) : 'the session'}.`} body="" /></div>
              : <div className="ft-empty">Select one or more strikes to plot their intraday flow for {session ? dayLabel(session) : 'the session'}.</div>)}
          </div>
          {chartSeries.length > 0 && (
            <div className="ft-legend" title="Click a line to mark its session high">
              {showUnd && undBars.length > 1 && (
                <span className="ft-legitem ft-legund" title={undCopy.hint}>
                  <span className="ft-legsw" style={{ borderTopColor: UND_COLOR, borderTopWidth: 2 }} />
                  <span className="ft-leglab ft-legundlab">{undCopy.name} · {undLabel}</span>
                  <span className="ft-legpx">{fmtPx(undBars[undBars.length - 1].c)}</span>
                </span>
              )}
              {chartSeries.map(ss => (
                <button key={ss.label} className={'ft-legitem' + (highs.has(ss.label) ? ' on' : '')} onClick={() => toggleHigh(ss.label)} title={(highs.has(ss.label) ? 'Hide' : 'Show') + ' high of ' + ss.label}>
                  <span className="ft-legsw" style={{ borderTopColor: ss.color, borderTopStyle: ss.dash.length ? 'dashed' : 'solid' }} />
                  <span className="ft-leglab">{ss.label}</span>
                  {highs.has(ss.label) && <span className="ft-leghi">▲</span>}
                </button>
              ))}
            </div>
          )}
          <div className="ft-focus">
            <div className="ft-ftitle">Focus <b>{focusK ? `${underlying} ${focusK}` : '—'}</b> · {isLive ? 'live' : dayLabel(session)}</div>
            <div className="ft-cells">
              {focusStrike ? ([['Call net', focusStrike.cNet >= 0 ? BULL_T : BEAR_T, focusStrike.cNet], ['Put net', focusStrike.pNet >= 0 ? BULL_T : BEAR_T, focusStrike.pNet], ['Call vol', MUTED, focusStrike.cVol], ['Put vol', MUTED, focusStrike.pVol], ['Net flow', focusStrike.net >= 0 ? BULL_T : BEAR_T, focusStrike.net]] as [string, string, number][]).map(([lab, col, val]) =>
                <div key={lab} className="ft-cell"><div className="ft-cl"><i style={{ background: col }} />{lab}</div><div className="ft-cv" style={{ color: (lab === 'Net flow' || lab.endsWith('net')) ? col : undefined }}>{fmtSign(val)}</div></div>
              ) : <span style={{ color: MUT2, fontSize: 12 }}>no strike focused</span>}
            </div>
          </div>
        </section>

        <FlowMoversPanel underlying={underlying} expiry={expiry} session={session} isLive={isLive} lastTick={lastTick} ladder={ladder} selected={selected} onPick={pickStrike} onPickOnly={pickOnly} onPickMany={pickMany} />
      </div>
      <div className="ft-foot"><b>RTL source · one RTH session.</b> Only the strikes you select are queried (per-strike net flow, not the whole chain). Switch days to load each session's 9:30–16:00 ET intraday on its own; scroll/drag to zoom &amp; pan.</div>
    </div>
  );
}

const FT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
.ft-root{--bg:#0e1015;--surface:#161a22;--surface-2:#1c212b;--raised:#212936;--line:#262d3a;--line-2:#323b4b;--text:#e7ebf2;--muted:#8b94a4;--muted-2:#5b6472;--bull:#16b981;--bear:#f0555f;--accent:#3f8ae6;--mono:'IBM Plex Mono',ui-monospace,Menlo,monospace;--sans:'IBM Plex Sans',system-ui,sans-serif;
  display:flex;flex-direction:column;height:100%;min-height:560px;background:var(--bg);color:var(--text);font-family:var(--sans);font-size:13px;border-radius:12px;overflow:hidden;border:1px solid var(--line)}
.ft-root *{box-sizing:border-box}
.ft-root button{font-family:inherit;color:inherit;cursor:pointer}
.ft-pos{color:var(--bull)}.ft-neg{color:var(--bear)}.ft-zero{color:var(--muted)}
.ft-top{display:flex;align-items:center;gap:12px;padding:10px 14px;background:linear-gradient(180deg,#12161e,#0f131a);border-bottom:1px solid var(--line);flex-wrap:wrap}
.ft-brand{display:flex;align-items:baseline;gap:9px}.ft-brand b{font-size:14px;font-weight:700;letter-spacing:.13em}.ft-accent{color:var(--accent)}.ft-brand small{color:var(--muted);font-size:10px}
.ft-field{display:flex;align-items:center;gap:7px}.ft-lbl{font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:var(--muted-2);font-weight:600}
.ft-pick{background:var(--surface);border:1px solid var(--line);border-radius:8px;color:var(--text);font-family:var(--mono);font-size:12.5px;padding:6px 9px;font-weight:500}
.ft-grow{flex:1}
.ft-day{display:flex;align-items:center;gap:6px}
.ft-daysel{min-width:132px}
.ft-nav{background:var(--surface);border:1px solid var(--line);border-radius:8px;color:var(--text);font-size:12px;padding:6px 9px;line-height:1}
.ft-nav:hover:not(:disabled){background:var(--raised)}.ft-nav:disabled{opacity:.32;cursor:default}
.ft-seg{display:flex;background:var(--surface);border:1px solid var(--line);border-radius:8px;overflow:hidden}
.ft-seg button{background:transparent;border:0;padding:6px 11px;font-size:12px;color:var(--muted);font-weight:500;border-right:1px solid var(--line)}
.ft-seg button:last-child{border-right:0}.ft-seg button.on{background:var(--raised);color:var(--text)}.ft-seg button:hover:not(.on):not(:disabled){color:var(--text)}
.ft-seg button:disabled{opacity:.35;cursor:not-allowed}
.ft-live{display:flex;align-items:center;gap:7px;font-size:11px;color:var(--muted);font-family:var(--mono)}
.ft-dot{width:8px;height:8px;border-radius:50%;background:var(--bull);box-shadow:0 0 0 0 rgba(22,185,129,.6);animation:ftpulse 1.7s infinite}
.ft-dot.paused{background:var(--muted-2);animation:none;box-shadow:none}
@keyframes ftpulse{0%{box-shadow:0 0 0 0 rgba(22,185,129,.5)}70%{box-shadow:0 0 0 7px rgba(22,185,129,0)}100%{box-shadow:0 0 0 0 rgba(22,185,129,0)}}
@media (prefers-reduced-motion:reduce){.ft-dot{animation:none}}
.ft-body{flex:1;display:flex;min-height:0}
.ft-rail{width:250px;flex:none;background:var(--surface);border-right:1px solid var(--line);display:flex;flex-direction:column;min-height:0}
.ft-rail-head{padding:9px 12px;border-bottom:1px solid var(--line);display:flex;align-items:center;justify-content:space-between}
.ft-hint{font-size:10px;color:var(--muted-2)}
.ft-ladder{overflow-y:auto;flex:1;padding:5px 6px}
.ft-note{color:var(--muted-2);font-size:11.5px;padding:14px 10px;text-align:center;line-height:1.5}
.ft-srow{display:grid;grid-template-columns:56px 1fr 54px;align-items:center;gap:8px;padding:7px 8px;border-radius:8px;cursor:pointer;border:1px solid transparent;position:relative}
.ft-srow:hover{background:var(--surface-2)}.ft-srow.sel{background:var(--surface-2);border-color:var(--line-2)}
.ft-srow.atm .ft-k::after{content:'ATM';position:absolute;font-size:8px;letter-spacing:.08em;color:var(--muted-2);transform:translate(4px,-11px)}
.ft-swatch{position:absolute;left:2px;top:8px;bottom:8px;width:3px;border-radius:2px;background:transparent}
.ft-srow.sel .ft-swatch{background:var(--sw,var(--accent))}
.ft-k{font-family:var(--mono);font-size:13px;font-weight:600;padding-left:6px}
.ft-split{height:14px;display:flex;border-radius:4px;overflow:hidden;background:#0c0e13}
.ft-c{background:var(--bull);opacity:.85}.ft-p{background:var(--bear);opacity:.85}
.ft-snet{font-family:var(--mono);font-size:12px;text-align:right;font-weight:600;font-variant-numeric:tabular-nums}
.ft-main{flex:1;display:flex;flex-direction:column;min-width:0;min-height:0}
.ft-tabs{display:flex;align-items:center;gap:10px;padding:9px 13px;border-bottom:1px solid var(--line);flex-wrap:wrap}
.ft-btn{background:var(--surface);border:1px solid var(--line);border-radius:8px;padding:5px 10px;font-size:11px;font-weight:600}.ft-btn:hover{background:var(--raised)}
.ft-check{display:flex;align-items:center;gap:6px;font-size:11.5px;color:var(--muted);cursor:pointer;user-select:none;padding:4px 9px;border:1px solid var(--line);border-radius:8px;background:var(--surface);font-weight:500}
.ft-check input{accent-color:var(--accent);cursor:pointer;margin:0}
.ft-check.off{opacity:.4;cursor:default}.ft-check:hover:not(.off){color:var(--text);border-color:var(--line-2)}
.ft-legend{display:flex;flex-wrap:wrap;gap:6px;padding:8px 2px 2px;align-items:center}
.ft-legitem{display:inline-flex;align-items:center;gap:6px;background:var(--surface);border:1px solid var(--line);border-radius:7px;padding:3px 9px;font-size:11.5px;color:var(--muted);font-weight:500;cursor:pointer;line-height:1.4}
.ft-legitem:hover{color:var(--text);border-color:var(--line-2)}
.ft-legitem.on{background:var(--raised);color:var(--text);border-color:var(--line-2)}
.ft-legsw{width:14px;height:0;border-top-width:2.5px;display:inline-block}
.ft-leglab{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11px}
.ft-leghi{font-size:9px;opacity:.9}
.ft-legund{cursor:default;color:var(--text)}.ft-legundlab{font-family:var(--sans);font-size:11.5px}.ft-legund:hover{border-color:var(--line)}
.ft-legpx{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11px;font-weight:600;color:#e7ebf2;opacity:.85}
.ft-undsw{width:12px;height:0;border-top:2px solid #e7ebf2;display:inline-block;opacity:.85}
.ft-tund{padding-bottom:4px;margin-bottom:4px;border-bottom:1px solid var(--line)}
.ft-metricname{margin-left:auto;font-size:11px;color:var(--muted)}.ft-metricname b{color:var(--text);font-weight:600}
.ft-zoomhint{color:var(--muted-2)}
.ft-chartwrap{flex:1;position:relative;min-height:0;padding:6px 6px 0}
.ft-chartwrap canvas{display:block;width:100%;height:100%;cursor:crosshair;touch-action:none}
.ft-tip{position:absolute;pointer-events:none;background:rgba(14,16,21,.94);border:1px solid var(--line-2);border-radius:9px;padding:8px 10px;font-size:11.5px;min-width:150px;box-shadow:0 8px 24px rgba(0,0,0,.5);opacity:0;transition:opacity .08s;z-index:5}
.ft-tt{font-family:var(--mono);color:var(--muted);font-size:10px;margin-bottom:6px}
.ft-tr{display:flex;align-items:center;gap:8px;justify-content:space-between;margin:2px 0}.ft-lg{display:flex;align-items:center;gap:7px}
.ft-sw{width:9px;height:9px;border-radius:2px;flex:none}.ft-val{font-family:var(--mono);font-weight:600}
.ft-empty{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:var(--muted-2);font-size:13px;text-align:center;padding:20px}
.ft-focus{border-top:1px solid var(--line);background:var(--surface);padding:10px 13px;display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.ft-ftitle{font-size:11px;color:var(--muted)}.ft-ftitle b{font-family:var(--mono);color:var(--text);font-size:13px}
.ft-cells{display:flex;gap:8px;flex-wrap:wrap}
.ft-cell{background:var(--bg);border:1px solid var(--line);border-radius:9px;padding:6px 11px;min-width:88px}
.ft-cl{font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted-2);display:flex;align-items:center;gap:5px}
.ft-cl i{width:7px;height:7px;border-radius:2px;display:inline-block}
.ft-cv{font-family:var(--mono);font-size:15px;font-weight:600;margin-top:2px;font-variant-numeric:tabular-nums}
.ft-foot{padding:8px 14px;border-top:1px solid var(--line);background:#0d1017;color:var(--muted-2);font-size:11px}.ft-foot b{color:var(--muted)}
.ft-tp{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted-2);font-weight:600;margin:5px 0 2px}
@media (max-width:760px){.ft-chartwrap.ft-two{min-height:340px}}
@media (max-width:760px){.ft-body{flex-direction:column}.ft-rail{width:auto;border-right:0;border-bottom:1px solid var(--line);max-height:168px}.ft-ladder{display:flex;flex-wrap:nowrap;overflow-x:auto}.ft-srow{grid-template-columns:1fr;min-width:118px}.ft-snet{text-align:left}.ft-zoomhint{display:none}}
`;
